import { useState } from "react";
import { useSelector } from "react-redux";

import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import FilterSingleSelectDropdown from "../../components/filterDropdown/FilterSingleSelectDropdown";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import { wipsScreenStatusOptions } from "../../shared/utility";
import { createSnackbarHelpers } from "../../shared/snackbar";
import { wipLabel } from "../../shared/tenantLabels";

export function WipsViewModel(logout, addSnackbar) {
  const tenant = useSelector((state) => state.auth.tenant);
  const wipEntityLabel = wipLabel(tenant);
  const defaultFilters = {
    room: { value: -1, label: "All rooms" },
    status: { value: 0, label: "Open" },
    startDate: null,
    endDate: null,
  };
  const [filters, setFilters] = useState(defaultFilters);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [wips, setWips] = useState({
    data: [],
  });
  const [showFilters, setShowFilters] = useState(false);
  const [rooms, setRooms] = useState([]);

  const { showSuccess, showError } = createSnackbarHelpers(addSnackbar);

  const getWips = async function (search, page, filter = filters) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
      filter: filter,
    };
    let callResult = await makePostApiCall(`wips`, payload);

    if (callResult.success) {
      const response = callResult.data;
      if (page > 1) {
        setWips({ ...response, data: [...wips.data, ...response.data] });
      } else {
        setLoading(false);
        setWips(response);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data?.message || "Could not fetch WIPs");
    }
  };

  const deleteWip = async function (wipId) {
    let callResult = await makePostApiCall(`deleteWip/${wipId}`, {});
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      getWips(null, 0, filters);
      showSuccess(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(callResult.data.message);
    }
  };

  const askDeleteWip = function (wip) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteWip(wip.id)}
          message={`Are you sure you want to delete ${wipEntityLabel} ${wip.lot_number}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const filterComponents = [
    <DateRangeFilter
      startDate={filters.startDate}
      endDate={filters.endDate}
      onChange={(name, value) =>
        setFilters({
          ...filters,
          [name]: value,
        })
      }
      resetFilter={() =>
        setFilters({
          ...filters,
          startDate: null,
          endDate: null,
        })
      }
    />,
    <FilterSingleSelectDropdown
      header="Room"
      onChange={(value) =>
        setFilters({
          ...filters,
          room: value,
        })
      }
      options={rooms}
      resetFilter={() =>
        setFilters({
          ...filters,
          room: { value: -1, label: "All rooms" },
        })
      }
      valueName="room"
      value={filters.room}
    />,
    <FilterSingleSelectDropdown
      header="Status"
      onChange={(value) =>
        setFilters({
          ...filters,
          status: value,
        })
      }
      options={wipsScreenStatusOptions}
      resetFilter={() =>
        setFilters({
          ...filters,
          status: { value: 0, label: "Open" },
        })
      }
      valueName="status"
      value={filters.status}
    />,
  ];

  const resetFilters = () => {
    setFilters(defaultFilters);

    setShowFilters(false);
    getWips(null, 1, defaultFilters);
  };

  const getRooms = async function () {
    let callResult = await makeGetApiCall(`roomsForDropdown`);
    if (callResult.success) {
      setRooms(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError("Could not fetch rooms");
    }
  };

  return {
    askDeleteWip,
    emptyModal,
    getWips,
    loading,
    wips,
    setEmptyModelOpen,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getRooms,
  };
}
